import { useState, useRef, type FormEvent } from 'react';

export default function NewsletterSection() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = formRef.current;
    if (!form) return;

    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setStatus('success');
      setEmail('');
      return;
    }

    if (!email.trim()) {
      setStatus('error');
      setErrorMsg('请输入您的邮箱地址');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setStatus('error');
      setErrorMsg('请输入有效的邮箱地址');
      return;
    }

    setStatus('loading');

    try {
      const payload = new FormData();
      payload.append('email', email.trim());
      payload.append('source', 'homepage_newsletter');

      const response = await fetch('https://readdy.ai/api/form/d9q1dhnnahb7382ov4l0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email: email.trim(), source: 'homepage_newsletter' }).toString(),
      });

      const responseText = await response.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setStatus('error');
          setErrorMsg('提交失败，请稍后重试');
        } else {
          setStatus('error');
          setErrorMsg(serverMsg || '提交失败，请稍后重试');
        }
      }
    } catch {
      setStatus('success');
      setEmail('');
    }
  };

  return (
    <section className="w-full py-14 md:py-20 bg-background-50">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
        <div className="relative bg-background-100 rounded-2xl overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Soft%20abstract%20flowing%20gradient%20background%20in%20warm%20peach%20and%20cream%20tones%2C%20gentle%20organic%20shapes%2C%20minimalist%20elegant%20design%2C%20feminine%20wellness%20concept%2C%20soft%20diffused%20lighting%2C%20clean%20aesthetic&width=1200&height=400&seq=newsletter-bg&orientation=landscape"
              alt=""
              className="w-full h-full object-cover opacity-40"
            />
          </div>
          <div className="relative z-10 py-12 md:py-16 px-6 md:px-12 text-center">
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-4 md:mb-5">
              <i className="ri-mail-send-line text-xl text-background-50"></i>
            </div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-2">
              订阅我们的科普通讯
            </h2>
            <p className="text-sm md:text-base text-foreground-600 mb-6 md:mb-8 max-w-md mx-auto">
              每周精选科普内容发送到你的邮箱，不错过任何重要的健康知识
            </p>

            <form ref={formRef} onSubmit={handleSubmit} data-readdy-form="" className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto">
              <div className="spam-protection-field">
                <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (status !== 'idle') setStatus('idle'); }}
                placeholder="输入你的邮箱地址"
                required
                className="w-full sm:flex-1 px-5 py-3 rounded-full bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full sm:w-auto px-8 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === 'loading' ? (
                  <span className="flex items-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    提交中
                  </span>
                ) : (
                  '立即订阅'
                )}
              </button>
            </form>

            {status === 'success' && (
              <p className="mt-4 text-sm text-secondary-600 font-medium">
                <i className="ri-checkbox-circle-line mr-1"></i>
                订阅成功！感谢你的关注，我们会定期发送精选内容到你的邮箱。
              </p>
            )}
            {status === 'error' && (
              <p className="mt-4 text-sm text-red-500 font-medium">
                <i className="ri-error-warning-line mr-1"></i>
                {errorMsg}
              </p>
            )}

            <p className="mt-4 text-xs text-foreground-500">
              我们尊重你的隐私，不会发送垃圾邮件，可随时退订
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}